import { useMemo } from 'react'

import { useFormContext } from 'react-hook-form'

import { Message } from '~/components/component-library'
import { useSwapPriceImpact } from '~/components/forms/swap/hooks/useSwapPriceImpact'
import { SwapFormValues } from '~/components/forms/swap/swapSchema'
import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { displayCurrency } from '~/utils/display'

type FormType = SwapFormValues | TransferFormValues

// If the price impact is less than this percentage AND greater than this amount, we show a warning
const MIN_PRICE_IMPACT_PERCENT = -2.5 // -2.5%
const MIN_PRICE_IMPACT_AMOUNT = -1 // $1.00

/**
 * This component displays a price impact warning message if the price impact is greater than the tolerance percentage
 */
export const SwapPriceImpactMessage = ({
  isLoadingQuote,
}: {
  isLoadingQuote?: boolean
}) => {
  useFormContext<FormType>()

  const { priceImpactUSD, priceImpactPercentage } = useSwapPriceImpact({
    isLoadingQuote,
  })

  const shouldShowMessage = useMemo(() => {
    if (priceImpactPercentage === null || priceImpactUSD === null) return false
    return (
      priceImpactPercentage < MIN_PRICE_IMPACT_PERCENT &&
      priceImpactUSD <= MIN_PRICE_IMPACT_AMOUNT
    )
  }, [priceImpactPercentage, priceImpactUSD])

  const messageText = useMemo(() => {
    if (priceImpactPercentage === null || priceImpactUSD === null) return null

    // Show the actual percentage (including negative values)
    const sign = priceImpactPercentage >= 0 ? '+' : '-'
    const percentageText = `${sign}${Math.abs(priceImpactPercentage).toFixed(2)}%`
    const usdText = `${sign}${displayCurrency(Math.abs(priceImpactUSD), {
      showLessThanForSmallValues: true,
    })}`

    return `Price impact warning ${percentageText} (${usdText})`
  }, [priceImpactPercentage, priceImpactUSD])

  if (!shouldShowMessage || !messageText) {
    return null
  }

  return <Message variant="warning">{messageText}</Message>
}
